import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
// Removed SizeIssueReport import - now using full page
import { 
  FaShoppingBag, 
  FaClock, 
  FaCheckCircle, 
  FaTruck, 
  FaBox,
  FaEye,
  FaTimes,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaRupeeSign,
  FaStar,
  FaCrown,
  FaEdit,
  FaCamera,
  FaTrash,
  FaImage
} from 'react-icons/fa';
import { useOrders } from '../context/OrderContext';
import { useAuth } from '../context/AuthContext';
import { useSizeIssueReport } from '../hooks/useSizeIssueReport';
import { uploadOptimizedImage } from '../utils/imageOptimization';
import { getFirestore, collection, addDoc, doc, updateDoc, increment, getDoc, deleteDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import './OrderHistory.css';
import ProductReviews from '../components/ProductReviews';
import Loader from '../components/Loader';
import SizeIssueStatus from '../components/SizeIssueStatus/SizeIssueStatus';

const OrderHistory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orders, loading, fetchUserOrders } = useOrders();
  const { currentUser } = useAuth();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successOrderId, setSuccessOrderId] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  // Removed modal state - now using full page navigation
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedProductForReview, setSelectedProductForReview] = useState(null);
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');

  // Flatten orders so each product gets its own order entry
  const flattenedOrders = React.useMemo(() => {
    if (!orders || orders.length === 0) return [];
    
    const flattened = [];
    orders.forEach(order => {
      if (order.items && order.items.length > 0) {
        order.items.forEach((item, itemIndex) => {
          flattened.push({
            ...order,
            id: `${order.id}_item_${itemIndex}`,
            originalOrderId: order.id,
            originalOrderNumber: order.orderNumber,
            items: [item], // Only this one item
            totalAmount: item.price * item.quantity,
            itemIndex: itemIndex
          });
        });
      } else {
        // If no items, keep the original order
        flattened.push(order);
      }
    });
    
    return flattened;
  }, [orders]);
  
  // Image upload states
  const [reviewImages, setReviewImages] = useState([]);
  const [imageUploadLoading, setImageUploadLoading] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState(0);
  const [imageUploadStatus, setImageUploadStatus] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [showReviewSuccess, setShowReviewSuccess] = useState(false);
  const [userReviews, setUserReviews] = useState({}); // Store user's reviews for products
  const [showReviewPopup, setShowReviewPopup] = useState(false);
  const [selectedReviewForPopup, setSelectedReviewForPopup] = useState(null);



  // Fetch user's existing reviews
  useEffect(() => {
    const fetchUserReviews = async () => {
      if (!currentUser?.uid) return;
      
      try {
        const reviewsRef = collection(db, 'reviews');
        const q = query(reviewsRef, where('userId', '==', currentUser.uid));
        const querySnapshot = await getDocs(q);
        
        const reviews = {};
        querySnapshot.forEach(doc => {
          const review = doc.data();
          reviews[review.productId] = {
            id: doc.id,
            ...review,
            createdAt: review.createdAt?.toDate() || new Date()
          };
        });
        
        setUserReviews(reviews);
      } catch (error) {
        // Error fetching user reviews
      }
    };

    fetchUserReviews();
  }, [currentUser?.uid]);

  // Check for success message from checkout
  useEffect(() => {
    // Check React Router state first
    if (location.state?.message && location.state?.orderId) {
      setShowSuccessMessage(true);
      setSuccessOrderId(location.state.orderId);
      // Clear the state to prevent showing message on refresh
      navigate(location.pathname, { replace: true });
    }
    // Check localStorage for fallback success message
    else {
      const storedMessage = localStorage.getItem('orderSuccessMessage');
      if (storedMessage) {
        try {
          const { message, orderId } = JSON.parse(storedMessage);
          setShowSuccessMessage(true);
          setSuccessOrderId(orderId);
          // Clear localStorage after showing message
          localStorage.removeItem('orderSuccessMessage');
        } catch (error) {
          localStorage.removeItem('orderSuccessMessage');
        }
      }
    }
  }, [location.state, navigate, location.pathname]);

  // Show success message when order is completed
  useEffect(() => {
    if (showSuccessMessage && successOrderId) {
      // Auto-dismiss after 6 seconds
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
      }, 6000);

      return () => clearTimeout(timer);
    }
  }, [showSuccessMessage, successOrderId]);

  useEffect(() => {
    if (currentUser) {
      fetchUserOrders();
    }
  }, [currentUser, fetchUserOrders]);

  // Redirect if not logged in
  useEffect(() => {
    if (!currentUser) {
      const currentPath = window.location.pathname;
      navigate(`/login?returnUrl=${encodeURIComponent(currentPath)}`);
    }
  }, [currentUser, navigate]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <FaClock className="status-icon pending" />;
      case 'confirmed':
        return <FaCheckCircle className="status-icon confirmed" />;
      case 'processing':
        return <FaBox className="status-icon processing" />;
      case 'shipped':
        return <FaTruck className="status-icon shipped" />;
      case 'delivered':
        return <FaCheckCircle className="status-icon delivered" />;
      case 'cancelled':
        return <FaClock className="status-icon cancelled" />;
      default:
        return <FaClock className="status-icon pending" />;
    }
  };

  const getStatusBadge = (status) => {
    const statusText = status.charAt(0).toUpperCase() + status.slice(1);
    return (
      <div className={`status-badge-premium ${status}`}>
        <div className="badge-shimmer"></div>
        <span>{statusText}</span>
      </div>
    );
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDeliveryDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDeliveryDateInfo = (order) => {
    if (order.status === 'delivered' && order.deliveryDate) {
      return {
        label: 'Delivered on',
        date: order.deliveryDate,
        icon: 'delivered'
      };
    } else if (order.estimatedDeliveryDate) {
      return {
        label: 'Expected delivery',
        date: order.estimatedDeliveryDate,
        icon: 'expected'
      };
    }
    return null;
  };

  const calculateTotal = (items) => {
    return items.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  const handleSizeIssueReport = (order) => {
    navigate('/size-issue-report', { 
      state: { 
        orderData: {
          id: order.id,
          orderNumber: order.orderNumber,
          productName: order.items?.[0]?.name || 'Product',
          size: order.items?.[0]?.size || '',
          email: order.userEmail,
          phone: order.shippingAddress?.phone || '',
          userId: order.userId
        }
      } 
    });
  };

  const closeOrderModal = () => {
    setShowOrderModal(false);
    setSelectedOrder(null);
  };

  // Check if user has already reviewed a product
  const hasUserReviewed = (productId) => {
    return userReviews[productId] !== undefined;
  };

  // Get user's review for a product
  const getUserReview = (productId) => {
    return userReviews[productId];
  };

  // Handle opening review popup
  const handleReviewPopup = (productId) => {
    const review = getUserReview(productId);
    if (review) {
      setSelectedReviewForPopup(review);
      setShowReviewPopup(true);
    }
  };

  const handleWriteReview = (product) => {
    
    // Find the order that contains this product
    const targetOrder = flattenedOrders.find(o => o.items.some(item => item.productId === product.productId));
    if (targetOrder) {
      setSelectedOrder(targetOrder);
      // Store the original order ID directly in the product object for immediate use
      product.orderId = targetOrder.originalOrderId || targetOrder.id;
      product.originalOrderId = targetOrder.originalOrderId || targetOrder.id;
    }
    
    setSelectedProductForReview(product);
    setShowReviewModal(true);
  };

  const handleSubmitReview = async () => {
    if (rating === 0) return;
    
    // Check if we have valid product data
    if (!selectedProductForReview?.productId) {
      alert('Product information not found. Please try again.');
      return;
    }
    
    setSubmittingReview(true);
    
    try {
      // Create review data
      const reviewData = {
        productId: selectedProductForReview.productId, // Use productId from order item
        productName: selectedProductForReview.name || 'Unknown Product',
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email,
        rating: rating,
        reviewText: reviewText.trim() || null, // Optional text
        images: reviewImages.length > 0 ? reviewImages.map(img => ({
          original: img.original || null,
          webp: img.webpUrl || null
        })) : [],
        createdAt: serverTimestamp(), // Use Firestore server timestamp
        orderId: selectedProductForReview.originalOrderId || selectedOrder?.id || null, // Link to the original order ID
        originalOrderId: selectedProductForReview.originalOrderId || null // Link to the original order
      };



      // Save to Firestore - use the imported db instance
      // const db = getFirestore(app, "vastrebyvijh-v1");
      
      // Validate the database instance
      if (!db) {
        throw new Error('Firestore database instance is null or undefined');
      }
      
      const reviewsRef = collection(db, 'reviews');
      
      // Validate the collection reference
      if (!reviewsRef) {
        throw new Error('Reviews collection reference is null or undefined');
      }
      
      // Skip the connection test and go straight to review submission
      
      // Add additional validation for review data
      if (!reviewData.productId || !reviewData.userId || !reviewData.rating) {
        throw new Error('Missing required review data: productId, userId, or rating');
      }
      
      const docRef = await addDoc(reviewsRef, reviewData);

      // Update product rating in products collection
      const productRef = doc(db, 'products', selectedProductForReview.productId);
      
      try {
        // First check if the product exists
        const productDoc = await getDoc(productRef);
        if (productDoc.exists()) {
          await updateDoc(productRef, {
            totalReviews: increment(1),
            averageRating: increment(rating)
          });
        } else {
          // Product does not exist in database, skipping product rating update
        }
      } catch (productUpdateError) {
        // Could not update product rating, continuing anyway - review was saved successfully
      }

      // Close modal and reset
      setShowReviewModal(false);
      setSelectedProductForReview(null);
      setRating(0);
      setReviewText('');
      setReviewImages([]);
      setSubmittingReview(false);
      
      // Show success message
      setShowReviewSuccess(true);
      
      // Reload order history to show updated review status
      await fetchUserOrders();
      
      // Also refresh user reviews to show the new review
      const userReviewsRef = collection(db, 'reviews');
      const userReviewsQuery = query(userReviewsRef, where('userId', '==', currentUser.uid));
      const userReviewsSnapshot = await getDocs(userReviewsQuery);
      
      const updatedReviews = {};
      userReviewsSnapshot.forEach(doc => {
        const review = doc.data();
        updatedReviews[review.productId] = {
          id: doc.id,
          ...review,
          createdAt: review.createdAt?.toDate() || new Date()
        };
      });
      
      setUserReviews(updatedReviews);
    } catch (error) {
      setSubmittingReview(false);
      
      // Show specific error messages
      if (error.code === 'permission-denied') {
        alert('Permission denied. Please check if you are logged in.');
      } else if (error.code === 'unavailable') {
        alert('Firebase is temporarily unavailable. Please try again.');
      } else if (error.code === 'invalid-argument') {
        alert('Invalid data format. Please check your review.');
      } else if (error.code === 'failed-precondition') {
        alert('Database operation failed. Please try again.');
      } else if (error.code === 'resource-exhausted') {
        alert('Database quota exceeded. Please try again later.');
      } else {
        alert(`Failed to submit review: ${error.message}`);
      }
    }
  };

  // Image upload functions
  const handleImageUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setImageUploadLoading(true);
    setImageUploadProgress(0);
    setImageUploadStatus('');
    
    try {
      const newImages = [];
      const totalFiles = files.length;
      
      for (let i = 0; i < totalFiles; i++) {
        const file = files[i];
        
        // Show start of file processing
        const startProgress = (i / totalFiles) * 80; // First 80% for file processing
        setImageUploadProgress(startProgress);
        
        // Upload and optimize image
        const imageData = await uploadOptimizedImage(
          file, 
          'reviews', 
          `review_${Date.now()}_${i}`,
          (progress, message) => {
            // Calculate overall progress: base progress + file-specific progress
            const baseProgress = (i / totalFiles) * 80;
            const fileProgress = (progress / 100) * (80 / totalFiles);
            const overallProgress = Math.min(baseProgress + fileProgress, 95);
            setImageUploadProgress(overallProgress);
            setImageUploadStatus(message);
          }
        );
        
        const newImage = {
          id: Date.now() + i,
          original: imageData.original,
          optimized: imageData.optimized,
          preview: URL.createObjectURL(file), // Show original for instant preview
          webpUrl: imageData.optimized // Store WebP for backend
        };
        newImages.push(newImage);
        
        // Show progress after each file is processed
        const fileProgress = ((i + 1) / totalFiles) * 80;
        setImageUploadProgress(fileProgress);
      }
      
      // Final 20% for completion
      setImageUploadProgress(100);
      
      setReviewImages(prev => {
        const updated = [...prev, ...newImages];
        return updated;
      });
      
      // Keep at 100% for a moment to show completion
      setTimeout(() => setImageUploadProgress(0), 1500);
      
    } catch (error) {
      alert('Failed to upload images. Please try again.');
    } finally {
      setImageUploadLoading(false);
    }
  };

  const removeImage = (imageId) => {
    setReviewImages(prev => prev.filter(img => img.id !== imageId));
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleImageUpload(files);
    }
  };

  if (!currentUser) {
    return null;
  }

  if (loading) {
    return (
      <div className="order-history-page">
        <div className="luxury-container">
          <div className="loading-container">
            <div className="luxury-loading-spinner">
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
            </div>
            <p>Loading your premium orders...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="order-history-page">
      {/* Luxury Background Pattern */}
      <div className="luxury-bg-pattern">
        <div className="monogram-watermark">V</div>
      </div>

      <div className="luxury-container">
        {/* Header */}
        <motion.div
          className="luxury-page-header"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1>Order History</h1>
        </motion.div>

        {/* Success Message */}
        {showSuccessMessage && (
          <motion.div
            className="success-message-container"
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <div className="success-message-content">
              <div className="success-message-icon">
                <FaCrown />
              </div>
              <div className="success-message-text">
                <h3>Order Placed Successfully!</h3>
                <p>Your order #{successOrderId} has been created and is now being processed.</p>
              </div>
              <button 
                className="success-message-close"
                onClick={() => setShowSuccessMessage(false)}
                aria-label="Close success message"
              >
                <FaTimes />
              </button>
              <div className="success-message-progress"></div>
            </div>
          </motion.div>
        )}

        {/* Luxury Timeline */}
        {orders.length === 0 ? (
          <motion.div
            className="no-orders"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="no-orders-content">
              <FaShoppingBag className="no-orders-icon" />
              <h2>No Orders Yet</h2>
              <p>Begin your bespoke journey with your first premium order.</p>
              <button 
                className="luxury-btn-primary"
                onClick={() => navigate('/collections')}
              >
                Start Shopping
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            className="luxury-timeline"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {/* Timeline Line */}
            <div className="timeline-line">
              <div className="timeline-accent"></div>
            </div>

            {/* Timeline Nodes */}
            {flattenedOrders.map((order, index) => (
              <motion.div
                key={order.id}
                className="timeline-node"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
              >
                {/* Timeline Connector */}
                <div className="node-connector">
                  <div className="connector-dot"></div>
                  <div className="connector-line"></div>
                </div>

                {/* Order Card */}
                <div className="luxury-order-card-premium" onClick={() => handleOrderClick(order)}>
                  {/* Order Header */}
                  <div className="order-header-premium">
                    <div className="order-number-premium">
                      <FaStar className="order-icon-premium" />
                      <h3>#{order.originalOrderNumber || order.orderNumber}{order.itemIndex !== undefined ? `-${order.itemIndex + 1}` : ''}</h3>
                    </div>
                    <div className="order-status">
                      {getStatusBadge(order.status)}
                    </div>
                  </div>

                  {/* Product Showcase */}
                  <div className="order-product-showcase-premium">
                    {order.items && order.items.length > 0 && (
                      <div className="product-preview-premium">
                        <img 
                          src={(() => {
                            const image = order.items[0].image;
                            if (image?.webp?.['400w']?.url) {
                              return image.webp['400w'].url;
                            } else if (image?.webp?.['800w']?.url) {
                              return image.webp['800w'].url;
                            } else if (image?.original?.url) {
                              return image.original.url;
                            } else {
                               return image || '/images/fallbacks/placeholder-product.webp';
                            }
                          })()} 
                          alt={order.items[0].name || 'Product'}
                          className="product-image-premium"
                        />
                        <div className="product-overlay">
                          <div className="product-count">
                            {/* Single item orders don't need count display */}
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="product-info-premium">
                      <h4 className="product-name-premium">
                        {order.items && order.items.length > 0 ? order.items[0].name : 'Product'}
                      </h4>
                      {order.items && order.items.length > 0 && (
                        <p className="product-variant-premium">
                          {order.items[0].variant && 
                          (order.items[0].variant.size || order.items[0].variant.color) ? 
                          `${order.items[0].variant.size || ''} ${order.items[0].variant.color || ''}`.trim() : 
                          ''}
                        </p>
                      )}
                      <div className="product-price-premium">
                        ₹{order.totalAmount?.toLocaleString() || calculateTotal(order.items || []).toLocaleString()}
                      </div>
                    </div>
                  </div>

                  {/* Order Meta Info */}
                  <div className="order-meta-premium">
                    <div className="meta-item-premium">
                      <FaCalendarAlt className="meta-icon-premium" />
                      <span>{formatDate(order.createdAt)}</span>
                    </div>
                    <div className="meta-item-premium">
                      <FaMapMarkerAlt className="meta-icon-premium" />
                      <span>{order.shippingAddress?.city}, {order.shippingAddress?.state}</span>
                    </div>
                    {getDeliveryDateInfo(order) && (
                      <div className="meta-item-premium delivery-date-premium">
                        <FaCalendarAlt className={`meta-icon-premium ${getDeliveryDateInfo(order).icon}`} />
                        <span>
                          <span className="delivery-label">{getDeliveryDateInfo(order).label}: </span>
                          {formatDeliveryDate(getDeliveryDateInfo(order).date)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Review Section for Delivered Orders */}
                  {order.status === 'delivered' && (
                    <div className="review-section-premium">
                      {(() => {
                        // Since each order now has only one product, simplify the logic
                        const product = order.items[0];
                        const hasReview = hasUserReviewed(product.productId);
                        
                        if (hasReview) {
                          // Product already reviewed - show existing review
                          return (
                            <motion.div
                              className="existing-review-compact clickable"
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: 0.2 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReviewPopup(product.productId);
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              <div className="existing-review-header">
                                <FaStar className="existing-review-star" />
                                <span className="existing-review-title">Reviewed</span>
                                <span className="existing-review-click-hint">Click to view</span>
                              </div>
                              <div className="existing-review-content">
                                <div className="existing-review-summary">
                                  <span className="review-summary-text">
                                    Your review
                                  </span>
                                </div>
                                <div className="existing-review-date">
                                  {getUserReview(product.productId)?.createdAt?.toLocaleDateString()}
                                </div>
                              </div>
                            </motion.div>
                          );
                        } else {
                          // Product not reviewed - show review button
                          return (
                            <motion.div
                              className="review-invitation-premium"
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: 0.2 }}
                            >
                              <div className="review-sparkle-premium">✨</div>
                              <motion.button
                                className="write-review-btn-premium"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleWriteReview(product);
                                }}
                                whileHover={{ 
                                  scale: 1.05, 
                                  y: -3,
                                  boxShadow: "0 8px 25px rgba(214, 164, 100, 0.4)"
                                }}
                                whileTap={{ scale: 0.95 }}
                              >
                                <div className="review-btn-content-premium">
                                  <FaStar className="review-btn-icon-premium" />
                                  <span>Share Your Experience</span>
                                  <div className="review-btn-glow-premium"></div>
                                </div>
                              </motion.button>
                            </motion.div>
                          );
                        }
                      })()}
                    </div>
                  )}

                  {/* Order Actions */}
                  <div className="order-actions">
                    <motion.button 
                      className="luxury-btn-view"
                      whileHover={{ scale: 1.02, y: -1 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <FaEye /> View Details
                    </motion.button>
                  </div>

                  {/* Size Issue Status - Shows report button or status */}
                  <SizeIssueStatus 
                    order={order} 
                    onReportIssue={handleSizeIssueReport}
                  />
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>

      {/* Premium Luxury Order Modal */}
      <AnimatePresence>
        {showOrderModal && selectedOrder && (
          <motion.div
            className="luxury-modal-overlay-premium"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeOrderModal}
          >
            <motion.div
              className="luxury-order-modal-premium"
              initial={{ opacity: 0, scale: 0.8, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 50 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Premium Modal Header */}
              <div className="modal-header-premium">
                <div className="modal-title-premium">
                  <FaCrown className="title-icon-premium" />
                  <h2>Order #{selectedOrder.orderNumber}</h2>
                </div>
                <button className="modal-close-premium" onClick={closeOrderModal}>
                  <FaTimes />
                </button>
              </div>

              {/* Premium Modal Content */}
              <div className="modal-content-premium">
                {/* Premium Order Status */}
                <div className="order-status-section-premium">
                  <div className="status-header-premium">
                    <h3 className="status-title-premium">Order Status</h3>
                    <div className="status-badge-modal-premium">
                      {selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1)}
                    </div>
                  </div>
                  <div className="status-description-premium">
                    {selectedOrder.status === 'pending' && 'Your order is being reviewed by our team'}
                    {selectedOrder.status === 'confirmed' && (
                      <div className="delivery-info-premium">
                        <span className="delivery-icon-premium">🚚</span>
                        <span className="delivery-text-premium">Expected delivery within</span>
                        <span className="delivery-days-premium">15-20 days</span>
                      </div>
                    )}
                    {selectedOrder.status === 'processing' && 'Your order is being crafted with precision'}
                    {selectedOrder.status === 'shipped' && 'Your order is on its way to you'}
                    {selectedOrder.status === 'delivered' && 'Your order has been delivered successfully'}
                    {selectedOrder.status === 'cancelled' && 'Your order has been cancelled'}
                  </div>
                </div>

                {/* Premium Order Details */}
                <div className="order-details-section-premium">
                  <h3 className="details-title-premium">Order Details</h3>
                  <div className="details-content-premium">
                    <div className="detail-row-premium">
                      <div className="detail-label-premium">
                        <FaCalendarAlt className="detail-icon-premium" />
                        <span>Order Date</span>
                      </div>
                      <div className="detail-value-premium">{formatDate(selectedOrder.createdAt)}</div>
                    </div>
                    <div className="detail-row-premium">
                      <div className="detail-label-premium">
                        <FaMapMarkerAlt className="detail-icon-premium" />
                        <span>Shipping Address</span>
                      </div>
                      <div className="detail-value-premium">{selectedOrder.shippingAddress?.address}, {selectedOrder.shippingAddress?.city}, {selectedOrder.shippingAddress?.state} {selectedOrder.shippingAddress?.pinCode}</div>
                    </div>
                    {getDeliveryDateInfo(selectedOrder) && (
                      <div className="detail-row-premium">
                        <div className="detail-label-premium">
                          <FaCalendarAlt className={`detail-icon-premium ${getDeliveryDateInfo(selectedOrder).icon}`} />
                          <span>{getDeliveryDateInfo(selectedOrder).label}</span>
                        </div>
                        <div className="detail-value-premium">{formatDeliveryDate(getDeliveryDateInfo(selectedOrder).date)}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Premium Order Items */}
                <div className="order-items-section-premium">
                  <h3 className="items-title-premium">Order Items</h3>
                  <div className="items-content-premium">
                    {selectedOrder.items?.map((item, index) => {
                      return (
                        <div key={index} className="item-card-premium">
                          <div className="item-content-premium">
                            <div className="item-image-premium">
                              {item.image ? (
                                <img 
                                  src={(() => {
                                    if (item.image?.webp?.['400w']?.url) {
                                      return item.image.webp['400w'].url;
                                    } else if (item.image?.webp?.['800w']?.url) {
                                      return item.image.webp['800w'].url;
                                    } else if (item.image?.original?.url) {
                                      return item.image.original.url;
                                    } else {
                                      return item.image;
                                    }
                                  })()} 
                                  alt={item.name || 'Product'}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div className="item-image-fallback" style={{ display: item.image ? 'none' : 'flex' }}>
                                <span className="fallback-icon">🛍️</span>
                              </div>
                            </div>
                          <div className="item-details-premium">
                            <h4 className="item-name-premium">{item.name}</h4>
                            {item.size && (
                              <p className="item-variant-premium">Size: {item.size}</p>
                            )}
                            <div className="item-price-premium">₹{item.price?.toLocaleString()}</div>
                            
                            {/* Add Review Button for Delivered Orders */}
                            {(() => {
                              return selectedOrder.status === 'delivered' && (
                                hasUserReviewed(item?.productId) ? (
                                  // Show existing review in compact way
                                  <div 
                                    className="existing-review-compact-modal clickable"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleReviewPopup(item?.productId);
                                    }}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    <div className="existing-review-header-modal">
                                      <FaStar className="existing-review-star-modal" />
                                      <span className="existing-review-title-modal">Your Review</span>
                                      <span className="existing-review-click-hint-modal">Click to view</span>
                                    </div>
                                    <div className="existing-review-content-modal">
                                      <div className="existing-review-rating-modal">
                                        {(() => {
                                          const review = getUserReview(item?.productId);
                                          return [1, 2, 3, 4, 5].map((star) => (
                                            <FaStar
                                              key={star}
                                              className={`existing-review-star-item-modal ${star <= review.rating ? 'filled' : 'empty'}`}
                                            />
                                          ));
                                        })()}
                                      </div>
                                      <div className="existing-review-date-modal">
                                        {getUserReview(item?.productId)?.createdAt?.toLocaleDateString()}
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <motion.button
                                    className="write-review-btn-premium"
                                    onClick={() => handleWriteReview(item)}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                  >
                                    <FaStar className="review-btn-icon-premium" />
                                    Write Review
                                  </motion.button>
                                )
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    );
                    })}
                  </div>
                </div>

                {/* Premium Order Summary */}
                <div className="order-summary-section-premium">
                  <h3 className="summary-title-premium">Order Summary</h3>
                  <div className="summary-content-premium">
                    <div className="summary-row-premium">
                      <span className="summary-label-premium">Subtotal</span>
                      <span className="summary-value-premium">₹{calculateTotal(selectedOrder.items || []).toLocaleString()}</span>
                    </div>
                    <div className="summary-row-premium">
                      <span className="summary-label-premium">Total</span>
                      <span className="summary-total-premium">₹{selectedOrder.totalAmount?.toLocaleString() || calculateTotal(selectedOrder.items || []).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Review Popup Modal - Show Existing Review */}
      <AnimatePresence>
        {showReviewPopup && selectedReviewForPopup && (
          <motion.div
            className="review-popup-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowReviewPopup(false)}
          >
            <motion.div
              className="review-popup-content"
              initial={{ scale: 0.8, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="review-popup-header">
                <h3>Your Review</h3>
                <button 
                  className="review-popup-close"
                  onClick={() => setShowReviewPopup(false)}
                >
                  ×
                </button>
              </div>
              
              <div className="review-popup-body">
                <div className="review-popup-rating">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <FaStar
                      key={star}
                      className={`review-popup-star ${star <= selectedReviewForPopup.rating ? 'filled' : 'empty'}`}
                    />
                  ))}
                  <span className="review-popup-rating-text">
                    {selectedReviewForPopup.rating} out of 5
                  </span>
                </div>
                
                {selectedReviewForPopup.reviewText && (
                  <div className="review-popup-text">
                    <p>"{selectedReviewForPopup.reviewText}"</p>
                  </div>
                )}
                
                {selectedReviewForPopup.images && selectedReviewForPopup.images.length > 0 && (
                  <div className="review-popup-images">
                    <h4>Review Images</h4>
                    <div className="review-popup-images-grid">
                      {selectedReviewForPopup.images.map((image, index) => {
                        // Use optimized WebP images with proper fallbacks
                        
                        // Use optimized WebP images with proper fallbacks
                        let imageUrl = null;
                        if (image?.webp?.['400w']?.url) {
                          imageUrl = image.webp['400w'].url;

                        } else if (image?.webp?.['800w']?.url) {
                          imageUrl = image.webp['800w'].url;

                        } else if (image?.webp?.['1080w']?.url) {
                          imageUrl = image.webp['1080w'].url;

                        } else if (image?.original?.url) {
                          imageUrl = image.original.url;

                        } else if (image?.url) {
                          imageUrl = image.url;

                        } else {
                          imageUrl = image;

                        }
                        
                        return (
                          <div key={index} className="review-popup-image-item">
                            <img 
                              src={imageUrl} 
                              alt={`Review image ${index + 1}`}
                              loading="lazy"
                              onError={(e) => {
                                // Try fallback URLs in order of preference
                                let fallbackUrl = null;
                                if (image?.webp?.['800w']?.url) {
                                  fallbackUrl = image.webp['800w'].url;
                                } else if (image?.webp?.['400w']?.url) {
                                  fallbackUrl = image.webp['400w'].url;
                                } else if (image?.original?.url) {
                                  fallbackUrl = image.original.url;
                                } else if (image?.url) {
                                  fallbackUrl = image.url;
                                } else {
                                  fallbackUrl = '/images/fallbacks/placeholder-product.webp';
                                }
                                
                                if (fallbackUrl && fallbackUrl !== imageUrl) {
                                  e.target.src = fallbackUrl;
                                } else {
                                  e.target.src = '/images/fallbacks/placeholder-product.webp';
                                }
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                <div className="review-popup-date">
                  Reviewed on {selectedReviewForPopup.createdAt?.toLocaleDateString()}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Review Modal for Orders - INSANE OUT OF BOX DESIGN! */}
      <AnimatePresence>
        {showReviewModal && (
          <motion.div
            className="order-review-modal-overlay-insane"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowReviewModal(false)}
          >
            <motion.div
              className="order-review-modal-insane"
              initial={{ scale: 0.3, rotateX: -90, y: 100 }}
              animate={{ scale: 1, rotateX: 0, y: 0 }}
              exit={{ scale: 0.3, rotateX: 90, y: -100 }}
              transition={{ duration: 0.8, type: "spring", bounce: 0.4 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Floating 3D Elements */}
              <div className="order-review-floating-elements">
                <motion.div 
                  className="order-review-floating-star"
                  animate={{ rotate: 360, y: [-10, 10, -10] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >⭐</motion.div>
                <motion.div 
                  className="order-review-floating-heart"
                  animate={{ scale: [1, 1.2, 1], x: [-5, 5, -5] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >💎</motion.div>
                <motion.div 
                  className="order-review-floating-crown"
                  animate={{ rotate: [0, 15, 0, -15, 0], y: [-8, 8, -8] }}
                  transition={{ duration: 4, repeat: Infinity }}
                >👑</motion.div>
              </div>

              {/* INSANE Header with 3D Product Display */}
              <div className="order-review-modal-header-insane">
                <div className="order-review-product-showcase">
                  <motion.div 
                    className="order-review-product-image-3d"
                    whileHover={{ rotateY: 15, scale: 1.05 }}
                    transition={{ type: "spring" }}
                  >
                    <img 
                      src={(() => {
                        const image = selectedProductForReview?.image;
                        if (image?.webp?.['400w']?.url) {
                          return image.webp['400w'].url;
                        } else if (image?.webp?.['800w']?.url) {
                          return image.webp['800w'].url;
                        } else if (image?.original?.url) {
                          return image.original.url;
                        } else {
                          return image;
                        }
                      })()} 
                      alt={selectedProductForReview?.name}
                      className="order-review-product-image-insane"
                    />
                    <div className="order-review-image-glow"></div>
                    <div className="order-review-image-shadow"></div>
                  </motion.div>
                  
                  <div className="order-review-product-info-insane">
                    <motion.h3 
                      className="order-review-product-title-insane"
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      {selectedProductForReview?.name}
                    </motion.h3>
                    
                  </div>
                </div>
                
                <motion.button 
                  className="order-review-close-btn-insane"
                  onClick={() => setShowReviewModal(false)}
                  whileHover={{ rotate: 180, scale: 1.2 }}
                  whileTap={{ scale: 0.8 }}
                >
                  <span className="order-review-close-icon">×</span>
                  <div className="order-review-close-ripple"></div>
                </motion.button>
              </div>
              
              <div className="order-review-modal-content-insane">
                {/* INSANE Rating Section with Dynamic Colors */}
                <div className="order-review-rating-section-insane">
                  <motion.label 
                    className="order-review-rating-label-insane"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    <span className="order-review-label-icon">🌟</span>
                    Rate Your Experience
                  </motion.label>
                  
                  <div className="order-review-stars-container-insane">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <motion.div
                        key={star}
                        className="order-review-star-wrapper-insane"
                        whileHover={{ 
                          scale: 1.3, 
                          y: -8
                        }}
                        whileTap={{ scale: 0.8 }}
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ delay: star * 0.1, type: "spring" }}
                      >
                        <FaStar
                          className="order-review-star-insane"
                          onClick={() => setRating(star)}
                                                      style={{ 
                              color: star <= rating 
                                ? (rating === 1 ? '#FF4444' : rating === 2 ? '#FF8800' : rating === 3 ? '#FFAA00' : rating === 4 ? '#FFCC00' : '#FFD700')
                                : '#E0E0E0',
                              filter: star <= rating 
                                ? `drop-shadow(0 0 15px ${rating === 1 ? '#FF4444' : rating === 2 ? '#FF8800' : rating === 3 ? '#FFAA00' : rating === 4 ? '#FFCC00' : '#FFD700'})`
                                : 'none',
                              cursor: 'pointer'
                            }}
                        />
                        <div className="order-review-star-trail"></div>
                        <div className="order-review-star-particles"></div>
                      </motion.div>
                    ))}
                  </div>
                  
                  <motion.div 
                    className="order-review-rating-text-insane"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                  >
                    {rating === 0 ? '✨ Select your rating ✨' : 
                     rating === 1 ? '😞 Poor Experience' :
                     rating === 2 ? '😐 Below Average' :
                     rating === 3 ? '😊 Good' :
                     rating === 4 ? '😍 Very Good' : '🤩 Excellent!'}
                  </motion.div>
                </div>
                
                {/* INSANE Review Text Section */}
                <div className="order-review-text-section-insane">
                  <motion.label 
                    className="order-review-text-label-insane"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 }}
                  >
                    <span className="order-review-label-icon">✍️</span>
                    Share Your Story (Optional)
                  </motion.label>
                  
                  <div className="order-review-textarea-container-insane">
                    <textarea
                      value={reviewText}
                      placeholder="Share your thoughts (optional)..."
                      rows={4}
                      onChange={(e) => setReviewText(e.target.value)}
                      className="order-review-textarea-insane"
                    />
                    <div className="order-review-textarea-glow"></div>
                    <motion.div 
                      className="order-review-floating-emoji"
                      animate={{ y: [-5, 5, -5], rotate: [0, 5, 0] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >🌟</motion.div>
                  </div>
                </div>

                {/* INSANE Image Upload Section with Main Loader */}
                <div className="order-review-image-upload-insane">
                  <motion.label 
                    htmlFor="order-review-images-input-insane" 
                    className="order-review-upload-label-insane"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.9 }}
                  >
                    <div className="order-review-upload-icon-container">
                      <FaCamera className="order-review-upload-icon-insane" />
                      <div className="order-review-upload-pulse"></div>
                    </div>
                    <span>Add Photos to Your Story</span>
                    <div className="order-review-upload-sparkles">✨📸✨</div>
                  </motion.label>
                  
                  <input
                    type="file"
                    id="order-review-images-input-insane"
                    accept="image/*"
                    multiple
                    onChange={handleFileSelect}
                    disabled={imageUploadLoading}
                  />
                  
                  {/* Upload More Hint */}
                  {reviewImages.length > 0 && (
                    <motion.div 
                      className="order-review-upload-more-hint"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      <p>💡 Click above to add more photos</p>
                    </motion.div>
                  )}
                  
                  {/* Progress Bar */}
                  {imageUploadLoading && (
                    <div className="order-review-upload-progress-insane">
                      <div className="order-review-progress-track-insane">
                        <motion.div 
                          className="order-review-progress-fill-insane"
                          initial={{ width: 0 }}
                          animate={{ width: `${imageUploadProgress}%` }}
                          transition={{ duration: 0.3 }}
                        />
                      </div>
                      <span className="order-review-progress-text-insane">{imageUploadProgress}%</span>
                    </div>
                  )}
                  

                  
                  {/* WebP Image Previews */}
                  {reviewImages.length > 0 && (
                    <motion.div 
                      className="order-review-uploaded-images-insane"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5 }}
                    >
                      {reviewImages.map((image, index) => (
                        <motion.div
                          key={image.id}
                          className="order-review-uploaded-image-insane"
                          initial={{ opacity: 0, x: -50, rotate: -15 }}
                          animate={{ opacity: 1, x: 0, rotate: 0 }}
                          transition={{ delay: index * 0.1, type: "spring" }}
                          whileHover={{ scale: 1.1, rotate: 5, y: -5 }}
                        >
                          <img 
                            src={image.preview} 
                            alt={`Review image ${image.id}`}
                            className="order-review-webp-preview"
                          />
                          <button
                            className="order-review-remove-image-insane"
                            onClick={() => removeImage(image.id)}
                            aria-label="Remove image"
                          >
                            <FaTrash />
                          </button>
                          <div className="order-review-image-glow-insane"></div>
                        </motion.div>
                      ))}
                    </motion.div>
                  )}
                </div>
              </div>
              
              {/* INSANE Action Buttons */}
              <div className="order-review-modal-actions-insane">
                <motion.button 
                  className="order-review-cancel-btn-insane"
                  onClick={() => setShowReviewModal(false)}
                  whileHover={{ scale: 1.05, x: -5 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span>Cancel</span>
                  <div className="order-review-btn-ripple"></div>
                </motion.button>
                                                  <motion.button 
                  className="order-review-submit-btn-insane"
                  onClick={handleSubmitReview}
                  disabled={rating === 0 || submittingReview}
                  whileHover={{ scale: 1.05, x: 5 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {submittingReview ? (
                    <div className="order-review-submit-loader">
                      <div className="order-review-submit-spinner"></div>
                      <span>Submitting...</span>
                    </div>
                  ) : (
                    <>
                      <span>Submit Review</span>
                      <div className="order-review-btn-particles"></div>
                      <div className="order-review-btn-glow"></div>
                    </>
                  )}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Image Processing Overlay */}
      {imageUploadLoading && (
        <div className="order-review-image-processing-overlay">
          <div className="order-review-processing-card">
            <div className="order-review-processing-icon">
              <FaImage />
              <div className="order-review-processing-pulse"></div>
            </div>
            <h3>Uploading Images</h3>
            <p>{imageUploadStatus || 'Please wait while we prepare your photos...'}</p>
            
            <div className="order-review-processing-progress">
              <div className="order-review-progress-bar">
                <motion.div 
                  className="order-review-progress-fill"
                  initial={{ width: 0 }}
                  animate={{ width: `${imageUploadProgress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
              <span className="order-review-progress-text">{imageUploadProgress}%</span>
            </div>
          </div>
        </div>
      )}

      {/* Review Success Message */}
      {showReviewSuccess && (
        <motion.div 
          className="order-review-success-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div 
            className="order-review-success-modal"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", damping: 20 }}
          >
            <div className="order-review-success-icon">
              <FaCheckCircle />
            </div>
            <h3>Review Submitted Successfully!</h3>
            <p>Thank you for sharing your experience with us.</p>
            <motion.button 
              className="order-review-success-btn"
              onClick={() => setShowReviewSuccess(false)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Continue
            </motion.button>
          </motion.div>
        </motion.div>
      )}

      {/* Size issue reporting now handled via full page navigation */}
    </div>
  );
};

export default OrderHistory;
